/**
 * Platform Security Settings — PLATFORM_ADMIN only.
 *
 * Manages the gateway token + tool lockdown policy. JWT signing is
 * platform-level (single global LEXIUN_SIGNING_KEY env var on lexserver
 * since the 2026-04-30 auth-pivot — see ai-docs/lexiun-auth-guide.md);
 * the page surfaces a read-only notice for it instead of an editable
 * field. None of these settings are exposed in the shared org-admin
 * library.
 *
 * Rendered as the "Security" tab in RuntimeOverview.
 */

import { AlertTriangle, Eye, EyeOff, Info, Lock, Plus, Save, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import { usePlatformApi } from "@/hooks/usePlatformApi";

const MASKED = "***MASKED***";

interface PlatformRuntimeConfig {
  security?: {
    // signingKey was removed by the 2026-04-30 auth-pivot (commit a23608f4);
    // JWT signing now uses the global LEXIUN_SIGNING_KEY env var on lexserver.
    // The wire still tolerates an unknown property here if the server ever
    // sends one, but this UI never reads or writes it.
    gatewayToken?: string;
  };
  tools?: {
    additionalDeny?: string[];
    additionalAllow?: string[];
  };
  updatedAt?: string;
  updatedBy?: string;
}

function SecretField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <label className="w-40 shrink-0 text-sm font-medium text-slate-300">{label}</label>
      <input
        type={visible ? "text" : "password"}
        // A masked SECRET field, not a login password. Chrome autofills a saved
        // site password into any input[type=password] it associates with the
        // origin — measured live on LexIDE 2026-09-13, where a stored Lexiun
        // password landed in the Telegram bot-token field and the operator saw
        // only dots. "off" is ignored on password-typed inputs; "new-password"
        // is the value browsers actually honour to suppress that fill.
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`flex-1 rounded-md border px-3 py-1.5 text-sm ${
          value === MASKED
            ? "border-green-700 bg-green-950 text-green-300"
            : "border-slate-600 bg-slate-800"
        }`}
      />
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        className="rounded p-1 text-slate-400 hover:text-slate-300"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function TagInput({
  label,
  tags,
  onChange,
}: {
  label: string;
  tags: string[];
  onChange: (tags: string[]) => void;
}) {
  const [input, setInput] = useState("");

  const add = () => {
    const v = input.trim();
    if (v && !tags.includes(v)) {
      onChange([...tags, v]);
      setInput("");
    }
  };

  const remove = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-300">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-slate-700 px-2.5 py-0.5 text-xs font-medium text-slate-200"
          >
            {tag}
            <button type="button" onClick={() => remove(tag)} className="text-slate-400 hover:text-red-400">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
            placeholder="Add tool name..."
            className="w-40 rounded border border-slate-600 bg-slate-800 px-2 py-0.5 text-xs"
          />
          <button type="button" onClick={add} className="rounded p-0.5 text-slate-400 hover:text-slate-200">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function PlatformSecuritySettings() {
  const { target, isLocalDev } = useRuntimeContext();
  const api = usePlatformApi();
  // In local dev mode, target is null — use fallback orgId
  const orgId = target?.orgId ?? (isLocalDev ? "dev-org" : undefined);

  // After the 2026-04-30 auth-pivot (commit a23608f4), `security.signingKey`
  // is no longer per-org — JWT signing uses a single global LEXIUN_SIGNING_KEY
  // env var. Only `gatewayToken` remains per-org. The state shape + PATCH
  // body reflect that narrower surface.
  const [security, setSecurity] = useState({ gatewayToken: "" });
  const [tools, setTools] = useState({ additionalDeny: [] as string[], additionalAllow: [] as string[] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [updatedInfo, setUpdatedInfo] = useState<{ at?: string; by?: string }>({});

  // Load
  // The page uses `?unmask=true` so the PATCH can echo the gateway token
  // back unchanged when only the deny/allow lists change. If the page
  // ever flips to mask-by-default, the save logic must be reworked to
  // PATCH only dirty fields — otherwise the literal "***MASKED***" string
  // would clobber the real gatewayToken.
  // See ai-docs/org-admin-runtime-config-plan.md §8.2.
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    api.getRuntimeConfig(orgId, { unmask: true })
      .then((res) => {
        const cfg = res.data as PlatformRuntimeConfig;
        setSecurity({
          gatewayToken: (cfg.security?.gatewayToken as string) ?? "",
        });
        setTools({
          additionalDeny: (cfg.tools?.additionalDeny as string[]) ?? [],
          additionalAllow: (cfg.tools?.additionalAllow as string[]) ?? [],
        });
        setUpdatedInfo({ at: cfg.updatedAt as string, by: cfg.updatedBy as string });
      })
      .catch(() => setMsg({ ok: false, text: "Failed to load config" }))
      .finally(() => setLoading(false));
  }, [orgId, api]);

  // TODO(verification): Q18 happy-path save was 401-blocked during
  // 2026-04-30 verification because the parallel agent's auth-pivot
  // SPA wiring (usePlatformApi + auth-exchange Bearer mint) was
  // mid-flight and uncommitted. The PATCH body shape was confirmed
  // correct via Playwright network inspection (`{"security":{},"tools":{...}}`
  // — signingKey absent), but the deny-tag-add → save → reload happy
  // path never went green end-to-end. Re-run that cycle once the
  // parallel agent's auth-pivot SPA work commits and remove this TODO.
  const handleSave = useCallback(async () => {
    if (!orgId) return;
    setSaving(true);
    setMsg(null);

    const patch: Partial<PlatformRuntimeConfig> = {};

    // Only send gatewayToken when the user actually edited it (i.e. the
    // field no longer holds the masked sentinel from the unmasked load).
    // signingKey was removed by the auth-pivot — never include it.
    const secPatch: Record<string, string | undefined> = {};
    if (security.gatewayToken !== MASKED) secPatch.gatewayToken = security.gatewayToken || undefined;
    if (Object.keys(secPatch).length > 0) patch.security = secPatch;

    // Always send tools (array replacement semantics)
    patch.tools = tools;

    try {
      await api.updateRuntimeConfig(orgId, patch);
      setMsg({ ok: true, text: "Security settings saved" });
      setTimeout(() => setMsg(null), 5000);
    } catch {
      setMsg({ ok: false, text: "Failed to save" });
    } finally {
      setSaving(false);
    }
  }, [orgId, security, tools, api]);

  if (!orgId) {
    return <p className="py-8 text-center text-sm text-slate-400">Select a runtime instance to configure security settings.</p>;
  }

  if (loading) {
    return <p className="py-8 text-center text-sm text-slate-400">Loading security settings...</p>;
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* Warning Banner */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-800 bg-amber-950/50 p-4">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div className="text-sm text-amber-200">
          <p className="font-medium">Platform Security Settings</p>
          <p className="mt-1 text-amber-300/80">
            Changes to the gateway token require container reprovision to take effect.
            Tool policy changes sync within 60 seconds.
          </p>
        </div>
      </div>

      {/* Security Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Lock className="h-4 w-4" /> Security Credentials
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/*
            JWT Signing Key — read-only notice. The 2026-04-30 auth-pivot
            (commit a23608f4) replaced the per-org signing key with a single
            global LEXIUN_SIGNING_KEY env var. The field used to be editable
            here but lexserver now suppresses it on read AND ignores it on
            write, so an editable input would have misled platform admins.
          */}
          <div className="flex items-start gap-2 rounded-md border border-slate-700 bg-slate-800/40 p-3 text-xs text-slate-300">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div>
              <div className="font-medium text-slate-200">JWT Signing Key</div>
              <p className="mt-0.5 text-slate-400">
                Managed at the platform level via the{" "}
                <code className="rounded bg-slate-900 px-1 py-0.5 font-mono text-[11px]">LEXIUN_SIGNING_KEY</code>{" "}
                env var on lexserver. To rotate, redeploy lexserver and all
                dependent runtimes with the new value. Per-org JWT signing
                keys were removed in the 2026-04-30 auth-flow pivot.
              </p>
            </div>
          </div>
          <SecretField
            label="Gateway Token"
            value={security.gatewayToken}
            onChange={(v) => setSecurity((p) => ({ ...p, gatewayToken: v }))}
          />
        </CardContent>
      </Card>

      {/* Tool Lockdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Tool Lockdown Policy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TagInput
            label="Additional Deny (blocked tools)"
            tags={tools.additionalDeny}
            onChange={(v) => setTools((p) => ({ ...p, additionalDeny: v }))}
          />
          <TagInput
            label="Additional Allow (extra permitted tools)"
            tags={tools.additionalAllow}
            onChange={(v) => setTools((p) => ({ ...p, additionalAllow: v }))}
          />
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-slate-700 pt-4">
        <div className="text-xs text-slate-500">
          {updatedInfo.at && (
            <span>Last updated: {new Date(updatedInfo.at).toLocaleString()}{updatedInfo.by ? ` by ${updatedInfo.by}` : ""}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {msg && (
            <span className={`text-xs ${msg.ok ? "text-green-400" : "text-red-400"}`}>{msg.text}</span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className={`h-4 w-4 ${saving ? "animate-pulse" : ""}`} />
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
